import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurrencyAmountDisplay } from 'src/app/shared/utils/currency/currency-format';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { RoomsResultsService } from 'src/app/core/services/rooms-results/rooms-results.service';
import { OperatorService } from 'src/app/core/services/operator/operator.service';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { GameId } from 'src/app/shared/models/game/game-id';
import { NavbarViewComponent, TabOption } from '../components/navbar-view/navbar-view.component';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NavbarViewComponent],
})
export class NavbarComponent implements OnInit {
  readonly gameName = input.required<string>();
  readonly gameId = input.required<GameId>();
  readonly tabs = input<TabOption[]>();
  readonly balance = input<CurrencyAmountDisplay>();

  readonly closeClicked = output<void>();
  readonly tabSelected = output<string>();

  OperatorService = OperatorService;
  operatorService = inject(OperatorService);
  themeService = inject(ThemeService);
  private roomsResultsService = inject(RoomsResultsService);
  private router = inject(Router);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  selectedTabIndex = 0;

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.updateSelectedTab(event.urlAfterRedirects);
      });
  }

  ngOnInit(): void {
    this.updateSelectedTab(this.router.url);
  }

  private updateSelectedTab(url: string): void {
    const tabs = this.tabs();
    if (!tabs) {
      return;
    }
    const path = url.startsWith('/') ? url.substring(1) : url;
    const index = tabs.findIndex((tab) => tab.id === path);
    if (index !== -1) {
      this.selectedTabIndex = index;
      this.changeDetectorRef.markForCheck();
    }
  }

  readonly displayedPendingCount = computed(() =>
    this.themeService.hasFtueResultsEnabled()
      ? this.roomsResultsService.pendingCount() || null
      : null,
  );

  readonly displayedShowDot = computed(
    () => this.themeService.hasFtueResultsEnabled() && this.roomsResultsService.hasPending(),
  );

  showBalance = computed(() => {
    const showBalance = this.themeService.header().hasBalance;
    if (this.operatorService.operatorType === 'session-adapter') {
      // Don't show the balance for session operators if the balance is not defined
      return showBalance && this.operatorService.formattedBalance() !== undefined;
    }
    return showBalance;
  });

  onCloseClicked() {
    this.closeClicked.emit();
  }

  onTabSelected(event: string) {
    this.tabSelected.emit(event);
  }
}
